import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { AppVersionService } from '@app/services/app-version.service';
import { selectedDashboardMode } from '@main-application/dashboards/actions/dashboard.actions';
import { TimezoneService } from '@main-application/management/components/system-configuration/components/date-time-configuration/timezone.service';
import { NotificationsService } from '@main-application/notifications/services/notifications.service';
import { loadEnumerations } from '@shared/actions/enumerations.actions';
import { loadTenantData, loadUserData, setAuthenticated } from '@shared/actions/user.actions';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { MainPageActions } from '@shared/enums/main-page-actions';
import { UserType } from '@shared/enums/user-type';
import { getDashboardMode } from '@shared/functions/get-dashboard-mode.function';
import { TenantData } from '@shared/interfaces/tenant-data';
import { UserData } from '@shared/interfaces/user-data';
import { selectDashboardViewType, selectLoadEnumerationsLoading } from '@shared/selectors/enumeration.selectors';
import { selectTenantData, selectUserData } from '@shared/selectors/user.selectors';
import { AuthService } from '@shared/services/auth.service';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { environment } from 'src/environments/environment';

import { ScreenWiperService } from '../../../services/screen-wiper.service';
import { SidePanelService } from '../../../services/side-panel.service';

@UntilDestroy()
@Component({
  selector: 'app-application-menu',
  templateUrl: './application-menu.component.html',
  styleUrls: ['./application-menu.component.scss'],
})
export class ApplicationMenuComponent extends ComponentAbstract implements OnInit, OnDestroy {
  screenWipeStatus$ = this.screenWiperService.screenClasses$;
  wipeDuration = this.screenWiperService.wipeDuration;
  wiperVisible$ = this.screenWiperService.globalWiperVisible$;
  environment = environment;
  isAuthenticated = false;
  videoUrl: string;
  isSearchSelected = false;
  showContent = true;
  currentYear: number;
  currentVersion = '';
  currentVersionTimeStamp = 0;

  showBuildTime = environment.showBuildTime;
  isExternal = false;
  displayNavigation = true; //Can be used for full screen mode in future

  isSidePanelExpanded$: Observable<boolean>;
  showBanner = false;

  constructor(
    protected cdr: ChangeDetectorRef,
    private authService: AuthService,
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private store: Store<{}>,
    private notificationsService: NotificationsService,
    private sidePanelService: SidePanelService,
    private snackbarService: SnackbarService,
    private appVersionService: AppVersionService,
    private activatedRoute: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private timezoneService: TimezoneService,
    private screenWiperService: ScreenWiperService
  ) {
    super(cdr);
  }

  ngOnInit() {
    this.showBanner = this.deviceService.browser === 'Safari';

    this.authService.watchForAccessToken().pipe(untilDestroyed(this)).subscribe();
    this.snackbarService.watchForSidePanel().pipe(untilDestroyed(this)).subscribe();

    this.isSidePanelExpanded$ = this.sidePanelService.isSidePanelExpanded$;

    this.appVersionService.version$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(appVersion => appVersion.version_timestamp > 0),
        first(),
        tap(appVersion => {
          this.currentVersionTimeStamp = appVersion.version_timestamp;
          this.currentVersion = appVersion.version_number;
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.checkSlackResponse();
        this.checkBuildiumResponse();
        this.cdr.detectChanges();
      });

    this.oidcSecurityService.isAuthenticated$
      .pipe(
        untilDestroyed(this),
        map(({ isAuthenticated }) => isAuthenticated),
        distinctUntilChanged()
      )
      .subscribe(isAuthenticated => {
        this.store.dispatch(setAuthenticated({ isAuthenticated }));

        if (!isAuthenticated) {
          this.notificationsService.close();
          return;
        }

        this.store.dispatch(loadTenantData());
        this.store.dispatch(loadUserData());
        this.store.dispatch(loadEnumerations());
        this.notificationsService.connect();
      });

    this.isLoading$ = this.store.select(selectLoadEnumerationsLoading);

    this.dashboardViewType$ = this.store.select(selectDashboardViewType);

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData)
      )
      .subscribe((userData: UserData) => {
        this.userData = userData;
        this.isExternal = this.userData.userType === UserType.Contractor;
        this.store.dispatch(selectedDashboardMode({ dashboardMode: getDashboardMode(userData) }));
      });

    this.store
      .select(selectTenantData)
      .pipe(
        untilDestroyed(this),
        filter((tenantData: TenantData) => !!tenantData?.tenantId),
        tap((tenantData: TenantData) => {
          const { href, hostname } = window.location;
          if (hostname !== 'localhost' && !href.includes(tenantData.tenantUrl)) {
            setTimeout(() => {
              const url = new URL(tenantData.tenantUrl);
              url.searchParams.append('revoke-token', '1');
              window.location.href = url.toString();
            }, 0);
          }
        })
      )
      .subscribe();

    this.currentYear = this.timezoneService.getCurrentDate().getFullYear();

    this.appVersionService.pull();
  }

  checkSlackResponse() {
    const { slackAdded } = this.activatedRoute.snapshot.queryParams;
    if (slackAdded === 'true') {
      this.removeQueryParam('slackAdded');
      setTimeout(() => {
        this.snackbarService.success(SnackbarErrorMessage.SlackAdded);
      }, 1000);
    }
  }

  checkBuildiumResponse() {
    const { buildium } = this.activatedRoute.snapshot.queryParams;
    if (buildium === 'true') {
      this.removeQueryParam('buildium');
      setTimeout(() => {
        this.snackbarService.success(SnackbarErrorMessage.BuildiumSetupCompleted);
      }, 1000);
    }
    if (buildium === 'false') {
      this.removeQueryParam('buildium');
      setTimeout(() => {
        this.snackbarService.error(SnackbarErrorMessage.BuildiumSetupIncompleted);
      }, 1000);
    }
  }

  removeQueryParam(paramName: string) {
    this.router.navigate([], {
      queryParams: {
        [paramName]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.notificationsService.close();
  }

  toggleSidePanel() {
    this.sidePanelService.toggleSidePanel();
  }

  mainPageActions(action: MainPageActions) {
    switch (action) {
      case MainPageActions.ACCOUNT:
        {
          // todo - redirect to account settings
        }
        break;
      case MainPageActions.PASSWORD:
        {
          // todo - redirect to password settings
        }
        break;
      case MainPageActions.NOTIFICATIONS:
        {
          // todo - redirect to notifications settings
        }
        break;
      case MainPageActions.LOGOUT:
        {
          this.oidcSecurityService.logoff();
          this.isAuthenticated = false;
        }
        break;
      case MainPageActions.LOGIN:
        {
          this.router
            .navigate([AppRoutes.LOGIN], {
              queryParams: {
                returnUrl: window.location.href,
              },
            })
            .then();
        }
        break;
      case MainPageActions.HOME_PAGE:
        {
          this.router.navigate([AppRoutes.HOME]).then();
        }
        break;
    }
  }
}
